import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Editor as TinyMCEEditor, RawEditorOptions, TinyMCE } from 'tinymce';
import { useTranslation } from 'react-i18next';

import { EditorHelpModal } from './partials';
import { importTinyDynamically } from './utils/tinymceChunkSplitter';
import { comment, standard } from './configurations/tinyConfiguration';
import { Plugin } from './plugins/Plugin';
import { globals } from '../../../../../lib/EnvironmentDetection';
import { getFirstParentWithClassName } from '../../../../../lib/window/Window';
import { normalizeHtml } from '../../../../../lib/Pipes/pipes';
import { linkchooserPlugin } from './plugins/Linkchooser';
import { replaceAllDownloadLinksWithA11yConformMetaData } from '../../../../../_lib/components/Wysiwyg';
import { LoadingSpinner } from '../../../../atoms/LoadingSpinner';
import { SonHelpPlugin } from './plugins/SonHelp';
import IConstraints from '../../../../../types/IContraints';
import EditorInputCounter from './partials/EditorInputCounter';
import { useEditorContentToModel } from './useEditorContentToModel';
import { getTextContent } from './utils/tinyUtils';
import { useAppContext } from '../../../../../contexts/AppContext/AppContext';
import { windowLogger } from '../../../../../_lib/Logger/windowLogger';

const eventsToBeStopped = ['dragend', 'dragover', 'draggesture', 'dragdrop', 'drop', 'drag'];

type EditorProps = {
  id: string;
  value: string;
  name: string;
  constraints?: IConstraints;
  onChange?: (event: Event) => void;
  onInitialized?: (target: EventTarget | null) => void;
  autoFocus?: boolean;
};

/**
 * Text Content from Tiny contains \n when content is deleted.
 * Therefor counter shows 1, although there is nothing left in editor.
 * */
const getEditorTextLength = (text: string): number => {
  if (text.includes('\n') && text.length === 1) {
    return 0;
  }
  return text.length;
};

export function Editor({ value, name, id, constraints, onChange, autoFocus }: EditorProps) {
  const { t } = useTranslation('Editor');
  const { userInfo } = useAppContext();

  const normalizedValue = normalizeHtml(value, replaceAllDownloadLinksWithA11yConformMetaData(t));

  const [tinyMCE, setTinyMCE] = useState<TinyMCE | null>(null);
  const [tinyEditorInstance, setTinyEditorInstance] = useState<TinyMCEEditor | null>(null);
  const [editorInitialized, setEditorInitialized] = useState<boolean>(false);
  const [mutationCount, setMutationCount] = useState(0);
  const [textContent, setTextContent] = useState(getTextContent(tinyEditorInstance));

  const containerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const saveToForModel = (content: string) => {
    if (onChange) {
      onChange({ target: { name, value: content } } as any);
    }
    setTextContent(getTextContent(tinyEditorInstance));
  };
  useEditorContentToModel(tinyEditorInstance, textareaRef, constraints?.maxLength, saveToForModel);

  const config = constraints?.htmlFeatureSet;

  /*
   * When Editor component is rendering for teh first time Tiny is not initialized
   * and we do not get content to initialize textContent state properly.
   * Therefor wew need this useEffect to initialize it correctly.
   * */
  useEffect(() => {
    const newTextContent = getTextContent(tinyEditorInstance);

    if (textContent !== newTextContent) {
      setTextContent(newTextContent);
    }
  }, [textContent, tinyEditorInstance]);

  useEffect(() => {
    importTinyDynamically()
      .then(tinyMce => {
        setTinyMCE(tinyMce);
      })
      .catch(e => {
        windowLogger.error(e);
      });
  }, []);

  /**
   * Creates an MutationObserver
   * While moving through the content-wrapper the Editor will lose its connection to the Iframe
   * Since the Editor will lose all it's changes, we have to reinitialize the editor
   * This is common browser behavior, so we'll need to handle it by observing changes within the content Wrapper
   */
  const createMutationObserver = useCallback((): MutationObserver => {
    const mutationObserver = new MutationObserver(() => {
      setMutationCount(prev => prev + 1);
    });
    // need to get the first content-wrapper parent now as its now possible to have more than one content wrapper one
    // a single page.
    const contentWrapper = getFirstParentWithClassName(id, 'content-wrapper');
    if (contentWrapper && mutationObserver) {
      mutationObserver.observe(contentWrapper, {
        childList: true,
      });
    }
    return mutationObserver;
  }, [id]);

  /**
   * disables drag and drop in tiny as of bug SON-5097
   */
  const stopEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const setupEditorInstance = useCallback((editorInstance: TinyMCEEditor) => {
    // remove keyboard shortcuts for h5 and h6 and link dialog
    editorInstance.shortcuts.remove('access+5');
    editorInstance.shortcuts.remove('access+6');
    editorInstance.shortcuts.remove('meta+k');
    editorInstance.on(eventsToBeStopped.join(' '), stopEvent);
  }, []);

  /**
   * For working color schemes we have to add
   *   - a son class to <html> => specificity vs :root declaration of colors of some other plugins
   *   - a <meta> element for scrollbars and other native UI stuff.
   */
  const addMetaTagAndClassForColorScheme = (editorInstance: TinyMCEEditor) => {
    const html = editorInstance.getDoc().getElementsByTagName('html')[0];
    const head = editorInstance.getDoc().getElementsByTagName('head')[0];
    const meta = editorInstance.getDoc().createElement('meta');
    meta.name = 'color-scheme';
    meta.content = 'dark light';
    head.appendChild(meta);
    html.classList.add('son');
  };

  useEffect(() => {
    if (tinyMCE) {
      const defaultConfiguration = {
        selector: `#${id}`,
        // eslint-disable-next-line camelcase
        license_key: 'gpl',
        language: 'de',
        // for color scheme info see _colors.scss
        // eslint-disable-next-line camelcase
        body_class: `wysiwyg wysiwyg--editor-content color-scheme-${userInfo?.settings?.colorScheme}`,
        setup: setupEditorInstance,
        // eslint-disable-next-line camelcase
        autosave_prefix: 'tinymce-autosave-{path}{query}-' + id,
        // eslint-disable-next-line camelcase
        iframe_aria_text: t('textEditor'),
      };

      const sonTinyConfig = config === 'comment' ? comment : standard;
      const configuration: RawEditorOptions = {
        ...defaultConfiguration,
        ...sonTinyConfig,
        // eslint-disable-next-line camelcase
        auto_focus: autoFocus ? true : undefined,
      };

      // This will load the tiny overrides after init!
      tinyMCE
        .init(configuration)
        .then(([instance]) => {
          setEditorInitialized(true);
          setTinyEditorInstance(instance);
          tinyMCE.DOM.loadCSS(`/assets/styles/style.editor-overrides.${globals.bundleHash}.css`);
          addMetaTagAndClassForColorScheme(instance);
        })
        .catch();
      return () => {
        tinyMCE.get(id)?.remove();
        setTinyEditorInstance(null);
        setEditorInitialized(false);
      };
    }
  }, [config, tinyMCE, id, setupEditorInstance, mutationCount]);

  useEffect(() => {
    if (editorInitialized) {
      const observer = createMutationObserver();
      return () => observer.disconnect();
    }
  }, [createMutationObserver, editorInitialized]);

  /**
   * Restyling the textarea for setCustomValidity. tiny overwrites display: block with inline css
   * to display: none. In that state it is not possible to show form error messages from form validation.
   * It has to be set here because when set earlier tiny clones settings into its div with role application.
   * That leads to incorrect behaviour on first load.
   */
  useEffect(() => {
    if (editorInitialized && textareaRef.current) {
      textareaRef.current.style.display = 'block';
      textareaRef.current.setAttribute('tabindex', '-1');
    }
  }, [editorInitialized]);

  return (
    <>
      <div className={`editor  ${editorInitialized ? '' : 'editor--invisible'}`}>
        <div className="editor__container">
          <EditorInputCounter maxLength={constraints?.maxLength || 0} currentCount={getEditorTextLength(textContent)}>
            <div className="editor__ref" ref={containerRef}>
              <EditorHelpModal config={config} />
              <textarea
                ref={textareaRef}
                name={name}
                id={id}
                className="editor__textarea"
                aria-hidden="true"
                maxLength={constraints?.maxLength}
                // using defaultValue instead of value here
                // because textarea has to be mutable
                defaultValue={normalizedValue}
                // textarea requires tabindex 0 for correct taborder and for focus trap
                // https://www.tiny.cloud/docs/tinymce/6/accessibility/#taborder
                tabIndex={0}
              />
              {tinyMCE &&
                [linkchooserPlugin, SonHelpPlugin(config)].map((plugin: any) => (
                  <Plugin key={`plugin-${plugin.name}`} {...plugin} instance={tinyMCE} />
                ))}
            </div>
          </EditorInputCounter>
        </div>
      </div>

      {!editorInitialized && (
        <div className="editor__loading-spinner-container">
          <div className="editor__loading-spinner">
            <LoadingSpinner />
          </div>
        </div>
      )}
    </>
  );
}
